
import img0 from './API_Permissions.png';
import img1 from './Share_Files.png';
  
const content = `# Sharing Files with External Users

## Objective

When a file is protected by OnData SecureFile, that file can be accessed by authorized users within your organization. When that file is shared with users external to 
your organization, the file should stay protected but at the same time be accessible by the designated users. OnData SecureFile offers two options for file sharing 
with external users: 1) sharing files with external users whose organization subscribes to OnData as well; 2) sharing files with external users who do not use OnData 
SecureFile. The purpose of this document is to illustrate how to share files with external users who do not use OnData SecureFile yet. For external users who already 
use OnData SecureFile, more security controls can be applied to the files. Such feature is going to be made available in future releases.  
  
**Note**: In order to share files securely with external users who do not use OnData SecureFile yet, your organization must use Microsoft Entra ID as your Identity 
Provider and integrate Microsoft Entra ID with OnData Portal to enable Single Sign-On (SSO). If your organization does not integrate Microsoft Entra ID with OnData 
Portal for SSO purpose, you will have to decrypt the OSF files before sharing them with external users. The documentation below assumes that your organization has 
integrated your Microsoft Entra ID tenant with your OnData Portal tenant. For details on how to integrate OnData Portal with Microsoft Entra ID for identity and 
access management, please refer to [**Microsoft Entra ID Integration**](reference_doc#how-to_guides/portal_configuration/microsoft_entra_id_integration). 
  
## 1. Configuring Microsoft Entra ID
  
To allow its users to share files with external users, an organization needs to configure the OnData Portal application within its Microsoft Entra ID tenant. Below are 
the specific steps:  
* Login to the Azure Portal, and select \`App registrations\`. Click on the \`OnData Portal\` application from the list of applications. From the left menu, click on 
the \`API permissions\` menu item  
* Add the user_impersonation permission for the Azure Rights Management Services API  
a. Under \`Configure permissions\`, click on the \`Add a permission\` button.  
b. Under the \`Microsoft APIs\` tab, click on the \`Azure Rights Management Services\`, and click on the \`Delegated permissions\` option.  
c. Select the \`user_impersonation\` and click on the \`Add permissions\` button to add the selected permission.  
  
* Add the User.Read permission for the Microsoft Graph API  
a. Under \`Configure permissions\`, click on the \`Add a permission\` button.  
b. Under the \`Microsoft APIs\` tab, click on the \`Microsoft Graph\`, and click on the \`Delegated permissions\` option.  
c. Search for the \`User.Read\` permission, and select that permission, then click on the \`Add permissions\` button to add the selected permission.  
  
* Add the User.Read permission for the Microsoft Graph API  
a. Under \`Configure permissions\`, click on the \`Add a permission\` button.  
b. Under the \`APIs my organization uses\` tab, search for the \`Microsoft Information Protection Sync Service\`, and click on that service.  
c. Click on the \`Application permissions\` option, and select the \`UnifiedPolicy.Tenant.Read\` permission, then click on the \`Add permissions\` button to add the selected permission.  
  
After all required permissions are added, you should have a list of permissions configured, as illustrated below.  
  
![API Permissions](${ img0 })  
  
* Login to the Azure Portal, and select \`App registrations\`. Click on the \`OnData Portal\` application from the list of applications. From the left menu, click on 
the \`Authentication\` menu item  
* Under \`Platform configurations\`, click on the \`Add a platform\` button. Select the \`Mobile and desktop applications\` option.  
* Choose the Redirect URI of \`https://login.microsoftonline.com/common/oauth2/nativeclient\`, and click on the \`Configure\` button to add the platform.  
* Under the \`Implicit grant and hybrid flows\` section, check the option of \`Access tokens (used for implicit flows)\`
* Click on the \`Save\` button to save the above configurations.  
  
## 2. Sharing Files Using the SecureFile App
  
To share files with external users, please first login to the OnData SecureFile App, then click on the \`Share Files\` option under the \`Encryption\` tab. Select one or 
more files to be shared. For the selected files, you can designate the permissions the external users may have on the files:
* Read Only
* Read and Edit
* Read, Edit, Copy and Print
* All Permissions
  
Next, you can designate one or more external users who can access the file with the permissions selected above. You can specify email addresses for those users, or you 
can specify an email domain. If you specify an email domain, all users within that email domain will have designated permissions to the shared files.  
  
![Share Files](${ img1 })  
  
If you would like to limit the access to certain timeframe, you can further choose an expiration date for such access. OnData SecureFile will generate a copy of the selected 
files to be shared. If you would like to designate an \`Output Directory\` for the generated files, you may browse to that directory. Otherwise, the files to be shared will 
be generated within the same folder as where the original OSF files are.  
  
Once the files are generated, you can share those protected files with the external users, using whatever approaches as desired, such as attaching to an email, or distributing 
with a file transfer solution.  
  
## 3. Opening Shared Files
  
After receiving a shared file, an external user may just double-click on the file and open it in a default application. The user will be prompted to login to Microsoft 
Entra ID. Enter a valid email address that belongs to one of the designated users when the file was shared, and click on the \`Next\` button. Instead of logging into 
Microsoft Entra ID with a password, the user can choose the option of \`Other ways to sign in\`. One of the options is to email a One-Time Passcode (OTP) to the user. Click 
on that option and check email to get the OTP code. Enter the OTP code on the screen, and click on the \`Sign in\` button to access the shared file. The user permissions 
and expiration date, if any, will be enforced automatically.  
  
  
  `
  
export default content;
